'use client';

import '@/styles/globals.css';
import '@/lib/polyfills/browser-polyfills';

import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';

import { DirectionProvider } from '@radix-ui/react-direction';
import { merge } from 'lodash';

import { DEFAULT_TIMEZONE } from '@/common/constants';
import { Theme } from '@/common/enums';
import { CrossTabSyncHandler } from '@/components/assistant-ui/cross-tab-sync-handler';
import { HitlProvider } from '@/components/assistant-ui/hitl/hitl-provider';
import { ToolGroupProvider } from '@/components/assistant-ui/tool-group-provider';
import { AccessibilityPluginSync } from '@/components/providers/accessibility-plugin-sync';
import { BrandingProvider } from '@/components/providers/branding-provider';
import { ConfigProvider, useConfig } from '@/components/providers/config-provider';
import { CrossTabSyncProvider } from '@/components/providers/cross-tab-sync-provider';
import {
  MessageDataComponentsProvider,
  type MessageDataComponents,
} from '@/components/providers/data-renderers-provider';
import { DebugModeProvider } from '@/components/providers/debug-mode-provider';
import { DevToolsProvider } from '@/components/providers/dev-tools-provider';
import { LightboxContainerProvider } from '@/components/providers/lightbox-container-provider';
import { NextIntlClientProvider } from '@/components/providers/next-intl-provider';
import { PlatformProvider } from '@/components/providers/platform-provider';
import { ReadOnlyProvider } from '@/components/providers/read-only-provider';
import {
  ReadOnlyRuntimeProvider,
  type ConversationMessage,
  type ConversationThread,
} from '@/components/providers/read-only-runtime-provider';
import { ScopedThemeProvider } from '@/components/providers/scoped-theme-provider';
import { StorageAccessProvider } from '@/components/providers/storage-access-provider';
import { TriggerContextProvider } from '@/components/providers/trigger-context-provider';
import { WidgetAnalyticsProvider } from '@/components/providers/widget-analytics-provider';
import { LiveRegionProvider } from '@/components/ui/live-region';
import { SkipToContent } from '@/components/ui/skip-to-content';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useOnMountCallback } from '@/hooks/use-on-mount-callback';
import { CloudLocalRuntimeProvider } from '@/lib/assistant-cloud-local';
import { ALL_MESSAGES } from '@/lib/constants/all-messages.generated';
import type { ClientSafeConfig } from '@/lib/types/tenant';
import { getTextDirection } from '@/lib/utils/locale';
import { initStandaloneAffiliate } from '@/lib/utils/standalone-affiliate';

import { Layout } from './bazak-assistant/layout';

function StandaloneAffiliateInit() {
  const config = useConfig();
  const standaloneAffiliateId = config.navigation?.affiliateConfig?.standaloneAffiliateId;

  useEffect(() => {
    initStandaloneAffiliate(standaloneAffiliateId);
  }, [standaloneAffiliateId]);

  return null;
}

export type AssistantMode = 'embed';

export interface BazakAssistantProps {
  mode?: AssistantMode;
  clientConfig?: ClientSafeConfig;
  widgetVersion?: string;
  debug?: boolean;
  disableAnalytics?: boolean;
  /** Load the thread list (triggers read-only mode — no composer) */
  onLoadThreads?: () => Promise<ConversationThread[]>;
  onLoadMessages?: (threadId: string) => Promise<ConversationMessage[]>;
  onReady?: () => void;
  defaultSidebarOpen?: boolean;
  onSidebarOpenChange?: (open: boolean) => void;
  onActiveThreadChange?: (threadId: string) => void;
  hideSidebar?: boolean;
  /** Host components, keyed by data-part name, that render data the host bakes
   *  onto messages (see {@link ConversationMessage.dataParts}). */
  dataComponents?: MessageDataComponents;
}

export function BazakAssistant({
  clientConfig,
  mode,
  widgetVersion,
  debug,
  disableAnalytics,
  onLoadThreads,
  onLoadMessages,
  onReady,
  defaultSidebarOpen,
  onSidebarOpenChange,
  onActiveThreadChange,
  hideSidebar,
  dataComponents,
}: BazakAssistantProps) {
  const isWidget = mode === 'embed';
  const platform = isWidget ? 'copilot' : 'app';
  const isReadOnly = !!onLoadThreads;

  // Read-only mode defers onReady to ReadOnlyRuntimeProvider (after data loads).
  useOnMountCallback(isReadOnly ? undefined : onReady);

  const layoutProps = { mode, defaultSidebarOpen, onSidebarOpenChange, hideSidebar };

  const runtimeContent = isReadOnly ? (
    <ReadOnlyRuntimeProvider
      onLoadThreads={onLoadThreads!}
      onLoadMessages={onLoadMessages}
      onReady={onReady}
      onActiveThreadChange={onActiveThreadChange}
    >
      <Layout {...layoutProps} />
    </ReadOnlyRuntimeProvider>
  ) : (
    <CloudLocalRuntimeProvider platform={platform}>
      <CrossTabSyncHandler />
      <Layout {...layoutProps} />
    </CloudLocalRuntimeProvider>
  );

  const content = (
    <MessageDataComponentsProvider dataComponents={dataComponents}>
      <PlatformProvider platform={platform}>
        <WidgetAnalyticsProvider
          isWidget={isWidget && !disableAnalytics && !isReadOnly}
          widgetVersion={widgetVersion}
        >
          <TriggerContextProvider>
            <LiveRegionProvider>
              <SkipToContent />
              {isWidget && <AccessibilityPluginSync />}
              {!isWidget && !isReadOnly && <StandaloneAffiliateInit />}
              <DebugModeProvider initialDebugMode={debug}>
                <DevToolsProvider>
                  <ReadOnlyProvider readOnly={isReadOnly}>
                    {isReadOnly ? (
                      <ToolGroupProvider>
                        <HitlProvider>{runtimeContent}</HitlProvider>
                      </ToolGroupProvider>
                    ) : (
                      <StorageAccessProvider>
                        <CrossTabSyncProvider>
                          <ToolGroupProvider>
                            <HitlProvider>{runtimeContent}</HitlProvider>
                          </ToolGroupProvider>
                        </CrossTabSyncProvider>
                      </StorageAccessProvider>
                    )}
                  </ReadOnlyProvider>
                </DevToolsProvider>
              </DebugModeProvider>
            </LiveRegionProvider>
          </TriggerContextProvider>
        </WidgetAnalyticsProvider>
      </PlatformProvider>
    </MessageDataComponentsProvider>
  );

  if (clientConfig) {
    return <ConfigProvider config={clientConfig}>{content}</ConfigProvider>;
  }

  return content;
}

export interface StandaloneWrapperProps extends PropsWithChildren {
  config: ClientSafeConfig;
  messages?: Record<string, unknown>;
  theme?: string;
  /** Fires when the wrapper's providers mount; for data-readiness use
   *  {@link BazakAssistantProps.onReady} instead. */
  onReady?: () => void;
}

export function StandaloneWrapper({
  children,
  config,
  messages: externalMessages,
  theme,
  onReady,
}: StandaloneWrapperProps) {
  const locale = config.meta?.language || 'en';
  const direction = getTextDirection(locale);
  const defaultTheme = (config.meta?.defaultTheme as Theme) || Theme.Light;
  const effectiveTheme = theme ?? defaultTheme;
  const timeZone = config.meta?.timezone ?? DEFAULT_TIMEZONE;

  useOnMountCallback(onReady);

  // Serialize→parse so stableTranslations only changes reference on content change,
  // not on every new config object — otherwise NextIntlClientProvider cascades
  // re-renders to all useTranslations() consumers.
  const translationsJson = useMemo(
    () => JSON.stringify(config.translations),
    [config.translations],
  );
  const stableTranslations = useMemo(() => JSON.parse(translationsJson), [translationsJson]);

  const mergedMessages = useMemo(() => {
    if (externalMessages) {
      return externalMessages;
    }

    return merge(
      {} as Record<string, unknown>,
      ALL_MESSAGES['en'] || {},
      locale !== 'en' ? ALL_MESSAGES[locale] || {} : {},
      stableTranslations?.['en'] || {},
      locale !== 'en' ? stableTranslations?.[locale] || {} : {},
    );
  }, [externalMessages, locale, stableTranslations]);

  const themeContainerRef = useRef<HTMLDivElement>(null);
  const [lightboxContainer, setLightboxContainer] = useState<HTMLDivElement | null>(null);

  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    themeContainerRef.current = node;
    setLightboxContainer(node);
  }, []);

  return (
    <ConfigProvider config={config}>
      <NextIntlClientProvider locale={locale} messages={mergedMessages} timeZone={timeZone}>
        <div
          ref={containerRefCallback}
          dir={direction}
          lang={locale}
          className="bazak-branding relative flex h-full max-h-full w-full flex-col overflow-hidden"
        >
          <ScopedThemeProvider containerRef={themeContainerRef} theme={effectiveTheme}>
            <DirectionProvider dir={direction}>
              <BrandingProvider>
                <LightboxContainerProvider value={lightboxContainer}>
                  <TooltipProvider>{children}</TooltipProvider>
                </LightboxContainerProvider>
              </BrandingProvider>
            </DirectionProvider>
          </ScopedThemeProvider>
        </div>
      </NextIntlClientProvider>
    </ConfigProvider>
  );
}
